import { EventEmitter, Directive, forwardRef, ElementRef, NgZone, Input, Output, Component, Inject, PLATFORM_ID, Renderer2, Optional, ViewChild, NgModule, InjectionToken } from '@angular/core';
import { isPlatformBrowser, DOCUMENT, CommonModule } from '@angular/common';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { timer } from 'rxjs';
import { loadLazyJsFile, loadLazyCssFile, closestPolifill } from '@techmore/global';

/**
 * @fileoverview added by tsickle
 * Generated from: lib/editor/editor.directive.ts
 * @suppress {checkTypes,constantProperty,extraRequire,missingOverride,missingReturn,unusedPrivateMembers,uselessCode} checked by tsc
 */
class FroalaEditorDirective {
    /**
     * @param {?} el
     * @param {?} zone
     */
    constructor(el, zone) {
        this.zone = zone;
        // editor options
        this._opts = {
            immediateAngularModelUpdate: false,
            angularIgnoreAttrs: null
        };
        this.SPECIAL_TAGS = ['img', 'button', 'input', 'a'];
        this.INNER_HTML_ATTR = 'innerHTML';
        this._hasSpecialTag = false;
        this._listeningEvents = [];
        this._editorInitialized = false;
        this._oldModel = null;
        // froalaModel directive as output: update model if editor contentChanged
        this.froalaModelChange = new EventEmitter();
        // froalaInit directive as output: send manual editor initialization
        this.froalaInit = new EventEmitter();
        // Begin ControlValueAccesor methods.
        this.onChange = (/**
         * @param {?} _
         * @return {?}
         */
        (_) => { });
        this.onTouched = (/**
         * @return {?}
         */
        () => { });
        /** @type {?} */
        const element = el.nativeElement;
        // check if the element is a special tag
        if (this.SPECIAL_TAGS.indexOf(element.tagName.toLowerCase()) != -1) {
            this._hasSpecialTag = true;
        }
        // jquery wrap and store element
        this._$element = ((/** @type {?} */ ($(element))));
        this.zone = zone;
    }
    // End ControlValueAccesor methods.
    // froalaEditor directive as input: store the editor options
    /**
     * @param {?} opts
     * @return {?}
     */
    set froalaEditor(opts) {
        this._opts = opts || this._opts;
    }
    // froalaModel directive as input: store initial editor content
    /**
     * @param {?} content
     * @return {?}
     */
    set froalaModel(content) {
        this.updateEditor(content);
    }
    // Form model content changed.
    /**
     * @param {?} content
     * @return {?}
     */
    writeValue(content) {
        this.updateEditor(content);
    }
    /**
     * @param {?} fn
     * @return {?}
     */
    registerOnChange(fn) { this.onChange = fn; }
    /**
     * @param {?} fn
     * @return {?}
     */
    registerOnTouched(fn) { this.onTouched = fn; }
    // Update editor with model contents.
    /**
     * @private
     * @param {?} content
     * @return {?}
     */
    updateEditor(content) {
        if (JSON.stringify(this._oldModel) == JSON.stringify(content)) {
            return;
        }
        if (!this._hasSpecialTag) {
            this._oldModel = content;
        }
        else {
            this._model = content;
        }
        if (this._editorInitialized) {
            if (!this._hasSpecialTag) {
                this._$element.froalaEditor('html.set', content);
            }
            else {
                this.setContent();
            }
        }
        else {
            if (!this._hasSpecialTag) {
                this._$element.html(content);
            }
            else {
                this.setContent();
            }
        }
    }
    // update model if editor contentChanged
    /**
     * @private
     * @return {?}
     */
    updateModel() {
        this.zone.run((/**
         * @return {?}
         */
        () => {
            /** @type {?} */
            let modelContent = null;
            if (this._hasSpecialTag) {
                /** @type {?} */
                const attributeNodes = this._$element[0].attributes;
                /** @type {?} */
                const attrs = {};
                for (let i = 0; i < attributeNodes.length; i++) {
                    /** @type {?} */
                    const attrName = attributeNodes[i].name;
                    if (this._opts.angularIgnoreAttrs && this._opts.angularIgnoreAttrs.indexOf(attrName) != -1) {
                        continue;
                    }
                    attrs[attrName] = attributeNodes[i].value;
                }
                if (this._$element[0].innerHTML) {
                    attrs[this.INNER_HTML_ATTR] = this._$element[0].innerHTML;
                }
                modelContent = attrs;
            }
            else {
                /** @type {?} */
                const returnedHtml = this._$element.froalaEditor('html.get');
                if (typeof returnedHtml === 'string') {
                    modelContent = returnedHtml;
                }
            }
            this._oldModel = modelContent;
            // Update froalaModel.
            this.froalaModelChange.emit(modelContent);
            // Update form model.
            this.onChange(modelContent);
        }));
    }
    // register event on jquery element
    /**
     * @private
     * @param {?} element
     * @param {?} eventName
     * @param {?} callback
     * @return {?}
     */
    registerEvent(element, eventName, callback) {
        if (!element || !eventName || !callback) {
            return;
        }
        this._listeningEvents.push(eventName);
        element.on(eventName, callback);
    }
    /**
     * @private
     * @return {?}
     */
    initListeners() {
        /** @type {?} */
        const self = this;
        // bind contentChange and keyup event to froalaModel
        this.registerEvent(this._$element, 'froalaEditor.contentChanged', (/**
         * @return {?}
         */
        function () {
            setTimeout((/**
             * @return {?}
             */
            function () {
                self.updateModel();
            }), 0);
        }));
        if (this._opts.immediateAngularModelUpdate) {
            this.registerEvent(this._editor, 'keyup', (/**
             * @return {?}
             */
            function () {
                setTimeout((/**
                 * @return {?}
                 */
                function () {
                    self.updateModel();
                }), 0);
            }));
        }
    }
    // register events from editor options
    /**
     * @private
     * @return {?}
     */
    registerFroalaEvents() {
        if (!this._opts.events) {
            return;
        }
        for (const eventName in this._opts.events) {
            if (this._opts.events.hasOwnProperty(eventName)) {
                this.registerEvent(this._$element, eventName, this._opts.events[eventName]);
            }
        }
    }
    /**
     * @private
     * @return {?}
     */
    createEditor() {
        if (this._editorInitialized) {
            return;
        }
        this.setContent(true);
        // Registering events before initializing the editor will bind the initialized event correctly.
        this.registerFroalaEvents();
        this.initListeners();
        // init editor
        this.zone.runOutsideAngular((/**
         * @return {?}
         */
        () => {
            this._$element.on('froalaEditor.initialized', (/**
             * @return {?}
             */
            () => {
                this._editorInitialized = true;
            }));
            this._editor = this._$element.froalaEditor(this._opts).data('froala.editor').$el;
        }));
    }
    /**
     * @private
     * @return {?}
     */
    setHtml() {
        this._$element.froalaEditor('html.set', this._model || '', true);
        // This will reset the undo stack everytime the model changes externally. Can we fix this?
        this._$element.froalaEditor('undo.reset');
        this._$element.froalaEditor('undo.saveStep');
    }
    /**
     * @private
     * @param {?=} firstTime
     * @return {?}
     */
    setContent(firstTime = false) {
        /** @type {?} */
        const self = this;
        // Set initial content
        if (this._model || this._model == '') {
            this._oldModel = this._model;
            if (this._hasSpecialTag) {
                /** @type {?} */
                const tags = this._model;
                // add tags on element
                if (tags) {
                    for (const attr in tags) {
                        if (tags.hasOwnProperty(attr) && attr != this.INNER_HTML_ATTR) {
                            this._$element.attr(attr, tags[attr]);
                        }
                    }
                    if (tags.hasOwnProperty(this.INNER_HTML_ATTR)) {
                        this._$element[0].innerHTML = tags[this.INNER_HTML_ATTR];
                    }
                }
            }
            else {
                if (firstTime) {
                    this.registerEvent(this._$element, 'froalaEditor.initialized', (/**
                     * @return {?}
                     */
                    function () {
                        self.setHtml();
                    }));
                }
                else {
                    self.setHtml();
                }
            }
        }
    }
    /**
     * @private
     * @return {?}
     */
    destroyEditor() {
        if (this._editorInitialized) {
            this._$element.off(this._listeningEvents.join(' '));
            this._editor.off('keyup');
            this._$element.froalaEditor('destroy');
            this._listeningEvents.length = 0;
            this._editorInitialized = false;
        }
    }
    /**
     * @private
     * @return {?}
     */
    getEditor() {
        if (this._$element) {
            return this._$element.froalaEditor.bind(this._$element);
        }
        return null;
    }
    // send manual editor initialization
    /**
     * @private
     * @return {?}
     */
    generateManualController() {
        /** @type {?} */
        const self = this;
        /** @type {?} */
        const controls = {
            initialize: this.createEditor.bind(this),
            destroy: this.destroyEditor.bind(this),
            getEditor: this.getEditor.bind(this),
        };
        this.froalaInit.emit(controls);
    }
    // TODO not sure if ngOnInit is executed after @inputs
    /**
     * @return {?}
     */
    ngOnInit() {
        // check if output froalaInit is present. Maybe observers is private and should not be used?? TODO how to better test that an output directive is present.
        if (!this.froalaInit.observers.length) {
            this.createEditor();
        }
        else {
            this.generateManualController();
        }
    }
    /**
     * @return {?}
     */
    ngOnDestroy() {
        this.destroyEditor();
    }
}
FroalaEditorDirective.decorators = [
    { type: Directive, args: [{
                // tslint:disable-next-line: directive-selector
                selector: '[froalaEditor]',
                providers: [{
                        provide: NG_VALUE_ACCESSOR, useExisting: forwardRef((/**
                         * @return {?}
                         */
                        () => FroalaEditorDirective)),
                        multi: true
                    }]
            },] }
];
/** @nocollapse */
FroalaEditorDirective.ctorParameters = () => [
    { type: ElementRef },
    { type: NgZone }
];
FroalaEditorDirective.propDecorators = {
    froalaEditor: [{ type: Input }],
    froalaModel: [{ type: Input }],
    froalaModelChange: [{ type: Output }],
    froalaInit: [{ type: Output }]
};
if (false) {
    /**
     * @type {?}
     * @private
     */
    FroalaEditorDirective.prototype._opts;
    /**
     * @type {?}
     * @private
     */
    FroalaEditorDirective.prototype._$element;
    /**
     * @type {?}
     * @private
     */
    FroalaEditorDirective.prototype.SPECIAL_TAGS;
    /**
     * @type {?}
     * @private
     */
    FroalaEditorDirective.prototype.INNER_HTML_ATTR;
    /**
     * @type {?}
     * @private
     */
    FroalaEditorDirective.prototype._hasSpecialTag;
    /**
     * @type {?}
     * @private
     */
    FroalaEditorDirective.prototype._editor;
    /**
     * @type {?}
     * @private
     */
    FroalaEditorDirective.prototype._model;
    /**
     * @type {?}
     * @private
     */
    FroalaEditorDirective.prototype._listeningEvents;
    /**
     * @type {?}
     * @private
     */
    FroalaEditorDirective.prototype._editorInitialized;
    /**
     * @type {?}
     * @private
     */
    FroalaEditorDirective.prototype._oldModel;
    /** @type {?} */
    FroalaEditorDirective.prototype.froalaModelChange;
    /** @type {?} */
    FroalaEditorDirective.prototype.froalaInit;
    /** @type {?} */
    FroalaEditorDirective.prototype.onChange;
    /** @type {?} */
    FroalaEditorDirective.prototype.onTouched;
    /**
     * @type {?}
     * @private
     */
    FroalaEditorDirective.prototype.zone;
}

/**
 * @fileoverview added by tsickle
 * Generated from: lib/component/froala-editor.component.ts
 * @suppress {checkTypes,constantProperty,extraRequire,missingOverride,missingReturn,unusedPrivateMembers,uselessCode} checked by tsc
 */
/**
 * 文本編輯器
 * 原名: froala-editor
 */
class FroalaEditorComponent {
    // tslint:disable-next-line:max-line-length
    // toolBtn ['fullscreen', 'bold', 'italic', 'underline', 'strikeThrough', 'subscript', 'superscript', '|', 'fontFamily', 'fontSize', 'color', 'inlineStyle', 'paragraphStyle', '|', 'paragraphFormat', 'align', 'formatOL', 'formatUL', 'outdent', 'indent', 'quote', '-', 'insertLink', 'insertImage', 'insertVideo', 'embedly', 'insertFile', 'insertTable', '|', 'emoticons', 'specialCharacters', 'insertHR', 'selectAll', 'clearFormatting', '|', 'print', 'spellChecker', 'help', 'html', '|', 'undo', 'redo']
    /**
     * @param {?} platformId
     * @param {?} render
     * @param {?} el
     * @param {?} document
     * @param {?} assets
     */
    constructor(platformId, render, el, document, assets) {
        this.platformId = platformId;
        this.render = render;
        this.el = el;
        this.document = document;
        this.assets = assets;
        /**
         * 插件載入完畢，可進行初始化
         */
        this.canInit = false;
        /**
         * 是否已經初始化完成
         */
        this.hasInited = false;
        /**
         * 是否可使用控件
         */
        this.disabled = false;
        this._placeholder = '請輸入要編輯的內容!';
        /**
         * 宿主指令初始化完成事件
         */
        this.froalaEditorDirectiveRendered = new EventEmitter();
        /**
         * 宿主元素繪製完成拋出事件
         */
        this.froalaEditorElRendered = new EventEmitter();
        /**
         * froalaModel 內容物
         */
        this._editorContent = '<div></div>';
        /**
         * 載入完成事件
         */
        this.initialized = new EventEmitter();
        /**
         * 顏色選擇器彈出視窗顯示事件
         */
        this.colorPickerPopupShow = new EventEmitter();
        /**
         * 連結插入彈出視窗顯示事件
         */
        this.linkInsertPopupShow = new EventEmitter();
        /**
         * 連結編輯彈出視窗顯示事件
         */
        this.linkEditorPopupShow = new EventEmitter();
        this.options = {
            aviaryKey: 'd3c7e599f4364c01a3be3dbc0eafbdbe',
            key: '4A5E5F5F4g1G3I3B2A3D6B3C3E4E2A2H-8GLGTF1DMJg1BWLd1PO==',
            fontSizeUnit: 'rem',
            // 原本是px ['8',    '9',   '10',   '11',   '12',  '14', '18',   '24',   '30',   '36',   '48',   '60',   '72',   '96']
            // fontSize: ['0.57', '0.64', '0.71', '0.78', '0.85', '1', '1.28', '1.71', '2.14', '2.57', '3.42', '4.28', '5.14', '6.85'], // base 14px
            fontSize: ['.8', '.9', '1', '1.1', '1.2', '1.4', '1.8', '2.4', '3', '3.6', '4.8', '6', '7.2', '9.6'],
            // base 10px
            fontSizeDefaultSelection: '1.4',
            language: 'zh_tw',
            placeholderText: this._placeholder,
            charCounterCount: true,
            heightMin: 400,
            toolbarInline: false,
            // tslint:disable-next-line: max-line-length
            toolbarButtons: ['fullscreen', 'bold', 'italic', 'underline', 'strikeThrough', 'subscript', 'superscript', '|', 'fontFamily', 'fontSize', 'color', 'inlineStyle', 'paragraphStyle', '|', 'paragraphFormat', 'align', 'formatOL', 'formatUL', 'outdent', 'indent', 'quote', '-', 'insertLink', 'insertImage', 'insertVideo', 'embedly', 'insertFile', 'insertTable', '|', 'specialCharacters', 'insertHR', 'selectAll', 'clearFormatting', '|', 'print', 'spellChecker', 'html', '|', 'undo', 'redo'],
            // tslint:disable-next-line: max-line-length
            toolbarButtonsXS: ['undo', 'redo', '|', 'bold', 'italic', 'underline', '-', 'fontFamily', 'fontSize', 'color', 'insertLink', 'insertImage'],
            toolbarSticky: false,
            iframe: true,
            iframeStyle: 'body.fr-view{font-family: "微軟正黑體", Microsoft JhengHei;}',
            iframeStyleFiles: ['https://maxcdn.bootstrapcdn.com/bootstrap/3.3.7/css/bootstrap.min.css'],
            fontFamily: {
                'Verdana, \'微軟正黑體\', \'Microsoft JhengHei\'': '微軟正黑體',
                'Arial,Helvetica,sans-serif': 'Arial',
                'Georgia,serif': 'Georgia',
                'Impact,Charcoal,sans-serif': 'Impact',
                'Tahoma,Geneva,sans-serif': 'Tahoma',
                '\'Times New Roman\', Times,serif': 'Times New Roman',
                'Verdana,Geneva,sans-serif': 'Verdana'
            },
            fileUploadMethod: 'POST',
            fileUploadParam: 'qqfile',
            imageUploadParam: 'qqfile',
            videoInsertButtons: ['videoBack', '|', 'videoByURL', 'videoEmbed'],
            linkAlwaysBlank: true,
            events: {
                'froalaEditor.initialized': (/**
                 * @param {?} e
                 * @param {?} editor
                 * @return {?}
                 */
                (e, editor) => {
                    this._froalaEditorEl = e.target;
                    this.froalaEditor = editor; // === this.$froalaEditor.data('froala.editor')
                    $.each($('a[data-cmd="fontSize"]'), (/**
                     * @param {?} i
                     * @param {?} v
                     * @return {?}
                     */
                    (i, v) => {
                        // const pxSize = Math.ceil(parseFloat(v.innerHTML) * 14) + '';
                        /** @type {?} */
                        const pxSize = Math.ceil(parseFloat(v.innerHTML) * 10) + '';
                        v.innerHTML = pxSize;
                        v.title = pxSize;
                    }));
                    $('label[for="' + this.name + '"]').on('click', (/**
                     * @return {?}
                     */
                    () => {
                        $(this._froalaEditorEl).froalaEditor('events.focus');
                    }));
                    this.hasInited = true;
                    this.initialized.emit();
                }),
                'froalaEditor.popups.show.colors.picker': (/**
                 * @param {?} e
                 * @param {?} editor
                 * @return {?}
                 */
                (e, editor) => {
                    this.colorPickerPopupShow.emit();
                    $('[data-cmd="textColor"][data-param1="REMOVE"]').on('click', (/**
                     * @param {?} e
                     * @return {?}
                     */
                    e => {
                        $(this._froalaEditorEl).froalaEditor('format.removeStyle', 'color');
                        // === this.froalaEditor.commands.exec('format.removeStyle', 'color')
                        // === this.froalaEditor.commands.exec('colors.text', 'REMOVE');
                    }));
                    $('[data-cmd="backgroundColor"][data-param1="REMOVE"]').on('click', (/**
                     * @param {?} e
                     * @return {?}
                     */
                    e => {
                        $(this._froalaEditorEl).froalaEditor('format.removeStyle', 'background-color');
                    }));
                }),
                'froalaEditor.popups.show.link.insert': (/**
                 * @param {?} e
                 * @param {?} editor
                 * @return {?}
                 */
                (e, editor) => {
                    this.linkInsertPopupShow.emit();
                }),
                'froalaEditor.popups.show.link.edit': (/**
                 * @param {?} e
                 * @param {?} editor
                 * @return {?}
                 */
                (e, editor) => {
                    this.linkEditorPopupShow.emit();
                }),
                //   'froalaEditor.file.uploaded': (e, editor, response) =>{
                //     const data = JSON.parse(response);
                //     return {link: data.thumbnailUrl};
                //   },
                //   'froalaEditor.file.error': (e, editor, error, response) => {
                //   },
                'froalaEditor.blur': (/**
                 * @param {?} e
                 * @param {?} editor
                 * @return {?}
                 */
                (e, editor) => {
                    // console.log(editor.selection.get());
                    this.touch(true);
                }),
            }
        };
        this.touch = (/**
         * @param {?} _
         * @return {?}
         */
        (_) => { });
        this.propagateChange = (/**
         * @param {?} _
         * @return {?}
         */
        (_) => { });
        this.isBrowser = isPlatformBrowser(this.platformId);
    }
    /**
     * 載入插件的方法
     * @param {?} document
     * @param {?} renderer
     * @param {?} parentEl
     * @param {?} assetsConfig
     * @return {?}
     */
    static loadPlugin(document, renderer, parentEl, assetsConfig) {
        FroalaEditorComponent.isPluginIniting = true;
        checkIfDepLoad((/**
         * @return {?}
         */
        () => {
            assetsConfig.scripts.forEach((/**
             * @param {?} sc
             * @return {?}
             */
            sc => {
                /** @type {?} */
                const sca = sc.split('/');
                /** @type {?} */
                const scName = sca[sca.length - 1];
                loadLazyJsFile(document, renderer, scName, sc);
            }));
            assetsConfig.styles.forEach((/**
             * @param {?} st
             * @return {?}
             */
            st => {
                /** @type {?} */
                const sta = st.split('/');
                /** @type {?} */
                const stName = sta[sta.length - 1];
                loadLazyCssFile(document, renderer, parentEl, stName, st);
            }));
        }));
        checkPluginCanUse((/**
         * @return {?}
         */
        () => {
            FroalaEditorComponent.isPluginInit = true;
            FroalaEditorComponent.isPluginIniting = false;
            FroalaEditorComponent.pulginInit.emit();
            FroalaEditorComponent.pulginInit.complete();
        }));
    }
    /**
     * 換位符
     * @param {?} val
     * @return {?}
     */
    set placeholder(val) {
        if (typeof val === 'string') {
            this._placeholder = val;
            this.options.placeholderText = this._placeholder;
        }
    }
    /**
     * 宿主指令
     * @param {?} d
     * @return {?}
     */
    set foralaDrt(d) {
        this._foralaDrt = d;
        if (d) {
            this.froalaEditorDirectiveRendered.emit();
        }
    }
    /**
     * 宿主指令
     * @return {?}
     */
    get foralaDrt() { return this._foralaDrt; }
    /**
     * 宿主元素
     * @param {?} e
     * @return {?}
     */
    set froalaEditorElRef(e) {
        if (e) {
            this._froalaEditorEl = e.nativeElement;
            this.froalaEditorElRendered.emit();
        }
    }
    /**
     * 宿主元素
     * @return {?}
     */
    get froalaEditorEl() { return this._froalaEditorEl; }
    /**
     * @param {?} val
     * @return {?}
     */
    set editorContent(val) {
        if (val) {
            if (val !== this._editorContent) {
                this._editorContent = val;
                this.propagateChange(this._editorContent);
                this.triggerDomChangeEvent();
            }
        }
        else {
            if ('<div></div>' !== this._editorContent) {
                this._editorContent = '<div></div>';
                this.propagateChange('');
                this.triggerDomChangeEvent();
            }
        }
    }
    /**
     * @return {?}
     */
    get editorContent() { return this._editorContent; }
    /**
     * @param {?} isDisabled
     * @return {?}
     */
    setDisabledState(isDisabled) {
        this.disabled = isDisabled ? true : false;
    }
    /**
     * @param {?} fn
     * @return {?}
     */
    registerOnChange(fn) {
        this.propagateChange = fn;
    }
    /**
     * @param {?} fn
     * @return {?}
     */
    registerOnTouched(fn) {
        this.touch = fn;
    }
    /**
     * @param {?} value
     * @return {?}
     */
    writeValue(value) {
        this.editorContent = value;
    }
    /**
     * @return {?}
     */
    ngAfterViewInit() {
        if (this.isBrowser) {
            if (!FroalaEditorComponent.isPluginInit && !FroalaEditorComponent.isPluginIniting) {
                if (isPluginLoaded()) {
                    FroalaEditorComponent.isPluginInit = true;
                }
                else {
                    FroalaEditorComponent.loadPlugin(this.document, this.render, this.el.nativeElement, this.assets || froalaAssetsDefaultConfig);
                }
            }
            if (!FroalaEditorComponent.isPluginInit) {
                FroalaEditorComponent.pulginInit.subscribe((/**
                 * @return {?}
                 */
                () => {
                    this.canInit = true;
                }));
            }
            else {
                this.canInit = true;
            }
        }
    }
    /**
     * @return {?}
     */
    triggerDomChangeEvent() {
        if (!this.isBrowser) {
            return;
        }
        if (!this.froalaEditorEl) {
            return;
        }
        /** @type {?} */
        let event;
        if (typeof (Event) === 'function') {
            event = new Event('change', { bubbles: true, cancelable: true });
        }
        else {
            event = document.createEvent('Event');
            event.initEvent('change', true, true);
        }
        this.froalaEditorEl.dispatchEvent(event);
    }
    /**
     * @param {?} changes
     * @return {?}
     */
    ngOnChanges(changes) {
        if (this.importCssFiles && this.importCssFiles.length) {
            this.options.iframeStyleFiles = this.options.iframeStyleFiles.concat(this.importCssFiles);
        }
        this.options = mergeDeep(this.options, this.foralaOptions);
    }
    /**
     * @return {?}
     */
    ngOnDestroy() {
        if (this.foralaDrt) {
            this.foralaDrt.ngOnDestroy();
        }
    }
}
/**
 * froala-editor 插件初始化完成後觸發
 */
FroalaEditorComponent.pulginInit = new EventEmitter();
/**
 * 全域的 froala-editor 插件初始化完成
 */
FroalaEditorComponent.isPluginInit = false;
/**
 * 全域的 froala-editor 插件在正初始化中
 */
FroalaEditorComponent.isPluginIniting = false;
FroalaEditorComponent.decorators = [
    { type: Component, args: [{
                selector: 'tm-froala-editor',
                template: "<ng-container *ngIf=\"canInit\">\r\n  <div *ngIf=\"editorContent\" [class.deny]=\"disabled\"\r\n  [froalaEditor]='options' [(froalaModel)]=\"editorContent\"\r\n  #froalaEditor></div>\r\n</ng-container>\r\n",
                providers: [
                    {
                        provide: NG_VALUE_ACCESSOR,
                        useExisting: forwardRef((/**
                         * @return {?}
                         */
                        () => FroalaEditorComponent)),
                        multi: true
                    }
                ],
                styles: ["::ng-deep .fr-popup label{padding-top:initial}::ng-deep .deny{cursor:not-allowed}::ng-deep .deny .fr-toolbar button.fr-command.fr-btn{pointer-events:none}::ng-deep .deny .fr-wrapper{pointer-events:none}:host{display:block}"]
            }] }
];
/** @nocollapse */
FroalaEditorComponent.ctorParameters = () => [
    { type: undefined, decorators: [{ type: Inject, args: [PLATFORM_ID,] }] },
    { type: Renderer2 },
    { type: ElementRef },
    { type: undefined, decorators: [{ type: Inject, args: [DOCUMENT,] }] },
    { type: undefined, decorators: [{ type: Optional }, { type: Inject, args: [FROALA_ASSETS,] }] }
];
FroalaEditorComponent.propDecorators = {
    foralaOptions: [{ type: Input }],
    importCssFiles: [{ type: Input }],
    disabled: [{ type: Input }],
    name: [{ type: Input }],
    placeholder: [{ type: Input }],
    froalaEditorDirectiveRendered: [{ type: Output }],
    foralaDrt: [{ type: ViewChild, args: [FroalaEditorDirective, { static: false },] }],
    froalaEditorElRendered: [{ type: Output }],
    froalaEditorElRef: [{ type: ViewChild, args: ['froalaEditor', { static: false },] }],
    initialized: [{ type: Output }],
    colorPickerPopupShow: [{ type: Output }],
    linkInsertPopupShow: [{ type: Output }],
    linkEditorPopupShow: [{ type: Output }]
};
if (false) {
    /**
     * froala-editor 插件初始化完成後觸發
     * @type {?}
     */
    FroalaEditorComponent.pulginInit;
    /**
     * 全域的 froala-editor 插件初始化完成
     * @type {?}
     */
    FroalaEditorComponent.isPluginInit;
    /**
     * 全域的 froala-editor 插件在正初始化中
     * @type {?}
     */
    FroalaEditorComponent.isPluginIniting;
    /**
     * 是否渲染端為瀏覽器
     * @type {?}
     * @protected
     */
    FroalaEditorComponent.prototype.isBrowser;
    /**
     * 插件載入完畢，可進行初始化
     * @type {?}
     */
    FroalaEditorComponent.prototype.canInit;
    /**
     * 是否已經初始化完成
     * @type {?}
     */
    FroalaEditorComponent.prototype.hasInited;
    /**
     * froalaEditor 設置參數
     * @type {?}
     */
    FroalaEditorComponent.prototype.foralaOptions;
    /**
     * 要另外匯入進 froalaEditor 的樣式表位址陣列
     * @type {?}
     */
    FroalaEditorComponent.prototype.importCssFiles;
    /**
     * 是否可使用控件
     * @type {?}
     */
    FroalaEditorComponent.prototype.disabled;
    /**
     * 表單控制項名稱
     * @type {?}
     */
    FroalaEditorComponent.prototype.name;
    /** @type {?} */
    FroalaEditorComponent.prototype._placeholder;
    /**
     * 宿主指令初始化完成事件
     * @type {?}
     */
    FroalaEditorComponent.prototype.froalaEditorDirectiveRendered;
    /**
     * 宿主指令
     * @type {?}
     * @private
     */
    FroalaEditorComponent.prototype._foralaDrt;
    /**
     * 宿主元素繪製完成拋出事件
     * @type {?}
     */
    FroalaEditorComponent.prototype.froalaEditorElRendered;
    /**
     * 宿主元素
     * @type {?}
     * @protected
     */
    FroalaEditorComponent.prototype._froalaEditorEl;
    /**
     * froalaEditor 物件
     * @type {?}
     * @private
     */
    FroalaEditorComponent.prototype.froalaEditor;
    /**
     * froalaModel 內容物
     * @type {?}
     */
    FroalaEditorComponent.prototype._editorContent;
    /**
     * 載入完成事件
     * @type {?}
     */
    FroalaEditorComponent.prototype.initialized;
    /**
     * 顏色選擇器彈出視窗顯示事件
     * @type {?}
     */
    FroalaEditorComponent.prototype.colorPickerPopupShow;
    /**
     * 連結插入彈出視窗顯示事件
     * @type {?}
     */
    FroalaEditorComponent.prototype.linkInsertPopupShow;
    /**
     * 連結編輯彈出視窗顯示事件
     * @type {?}
     */
    FroalaEditorComponent.prototype.linkEditorPopupShow;
    /** @type {?} */
    FroalaEditorComponent.prototype.options;
    /** @type {?} */
    FroalaEditorComponent.prototype.touch;
    /** @type {?} */
    FroalaEditorComponent.prototype.propagateChange;
    /**
     * @type {?}
     * @protected
     */
    FroalaEditorComponent.prototype.platformId;
    /**
     * @type {?}
     * @protected
     */
    FroalaEditorComponent.prototype.render;
    /**
     * @type {?}
     * @protected
     */
    FroalaEditorComponent.prototype.el;
    /**
     * @type {?}
     * @protected
     */
    FroalaEditorComponent.prototype.document;
    /**
     * @type {?}
     * @protected
     */
    FroalaEditorComponent.prototype.assets;
}
/**
 * @return {?}
 */
function isPluginLoaded() {
    return jQuery.prototype.froalaEditor;
}
/** @type {?} */
const checkPluginCanUse = (/**
 * @param {?} cb
 * @return {?}
 */
(cb) => {
    /** @type {?} */
    const cpTmSubc = timer(0, 300).subscribe((/**
     * @param {?} t
     * @return {?}
     */
    t => {
        if (isPluginLoaded()) {
            cpTmSubc.unsubscribe();
            cb();
        }
    }));
});
const ɵ0 = checkPluginCanUse;
/**
 * @return {?}
 */
function isDepLoadeed() {
    return 'undefined' !== typeof window['jQuery'];
}
/**
 * @param {?} cb
 * @return {?}
 */
function checkIfDepLoad(cb) {
    /** @type {?} */
    const cdTmSubc = timer(0, 300).subscribe((/**
     * @param {?} t
     * @return {?}
     */
    t => {
        if (isDepLoadeed()) {
            cdTmSubc.unsubscribe();
            cb();
        }
    }));
}
/**
 * @param {?} item
 * @return {?}
 */
function isObject(item) {
    return (item && typeof item === 'object' && !Array.isArray(item));
}
/**
 * @param {?} target
 * @param {?} source
 * @return {?}
 */
function mergeDeep(target, source) {
    /** @type {?} */
    let output = Object.assign({}, target);
    if (isObject(source) && isObject(target)) {
        Object.keys(source).forEach((/**
         * @param {?} key
         * @return {?}
         */
        key => {
            if (isObject(source[key])) {
                if (!(key in target)) {
                    Object.assign(output, { [key]: source[key] });
                }
                else {
                    output[key] = mergeDeep(target[key], source[key]);
                }
            }
            else {
                Object.assign(output, { [key]: source[key] });
            }
        }));
    }
    return output;
}

/**
 * @fileoverview added by tsickle
 * Generated from: lib/view/view.directive.ts
 * @suppress {checkTypes,constantProperty,extraRequire,missingOverride,missingReturn,unusedPrivateMembers,uselessCode} checked by tsc
 */
class FroalaViewDirective {
    /**
     * @param {?} renderer
     * @param {?} element
     */
    constructor(renderer, element) {
        this.renderer = renderer;
        this._element = element.nativeElement;
    }
    // update content model as it comes
    /**
     * @param {?} content
     * @return {?}
     */
    set froalaView(content) {
        this._element.innerHTML = content;
    }
    /**
     * @return {?}
     */
    ngAfterViewInit() {
        this.renderer.addClass(this._element, 'fr-view');
    }
}
FroalaViewDirective.decorators = [
    { type: Directive, args: [{
                // tslint:disable-next-line: directive-selector
                selector: '[froalaView]'
            },] }
];
/** @nocollapse */
FroalaViewDirective.ctorParameters = () => [
    { type: Renderer2 },
    { type: ElementRef }
];
FroalaViewDirective.propDecorators = {
    froalaView: [{ type: Input }]
};
if (false) {
    /**
     * @type {?}
     * @private
     */
    FroalaViewDirective.prototype._element;
    /**
     * @type {?}
     * @private
     */
    FroalaViewDirective.prototype.renderer;
}

/**
 * @fileoverview added by tsickle
 * Generated from: lib/view/view.module.ts
 * @suppress {checkTypes,constantProperty,extraRequire,missingOverride,missingReturn,unusedPrivateMembers,uselessCode} checked by tsc
 */
class FroalaViewModule {
    /**
     * @return {?}
     */
    static forRoot() {
        return { ngModule: FroalaViewModule, providers: [] };
    }
}
FroalaViewModule.decorators = [
    { type: NgModule, args: [{
                declarations: [FroalaViewDirective],
                exports: [FroalaViewDirective]
            },] }
];

/**
 * @fileoverview added by tsickle
 * Generated from: lib/editor/editor.module.ts
 * @suppress {checkTypes,constantProperty,extraRequire,missingOverride,missingReturn,unusedPrivateMembers,uselessCode} checked by tsc
 */
class FroalaEditorModule {
    /**
     * @return {?}
     */
    static forRoot() {
        return { ngModule: FroalaEditorModule, providers: [] };
    }
}
FroalaEditorModule.decorators = [
    { type: NgModule, args: [{
                declarations: [FroalaEditorDirective],
                exports: [FroalaEditorDirective]
            },] }
];

/**
 * @fileoverview added by tsickle
 * Generated from: lib/tm-froala-editor.module.ts
 * @suppress {checkTypes,constantProperty,extraRequire,missingOverride,missingReturn,unusedPrivateMembers,uselessCode} checked by tsc
 */
/** @type {?} */
const FROALA_ASSETS = new InjectionToken('FROALA_ASSETS');
/** @type {?} */
const froalaAssetsDefaultConfig = {
    scripts: ['froala_editor.bundle.js'],
    styles: ['css/froala_editor.bundle.css']
};
/**
 * [angular-froala-wysiwyg](https://github.com/froala/angular-froala-wysiwyg.git\@3.1.2) 包裝模組，
 * 在引用套件的模組提供 FROALA_ASSETS 這個依賴項目可以覆寫掉預設的資產載入配置
 * ```typescript
 * {provide: FROALA_ASSETS, useValue: {
 *   scripts: ['froala_editor.bundle.js'],
 *   styles:  ['css/froala_editor.bundle.css']
 * }}
 * ```
 */
class TmFroalaEditorModule {
    constructor() {
        closestPolifill();
    }
    /**
     * @return {?}
     */
    static forRoot() {
        return {
            ngModule: TmFroalaEditorModule,
            providers: [...FroalaEditorModule.forRoot().providers, ...FroalaViewModule.forRoot().providers]
        };
    }
}
TmFroalaEditorModule.decorators = [
    { type: NgModule, args: [{
                declarations: [
                    FroalaEditorComponent
                ],
                imports: [
                    CommonModule,
                    FroalaEditorModule,
                    FroalaViewModule,
                ],
                exports: [
                    FroalaEditorComponent,
                    FroalaEditorModule,
                    FroalaViewModule,
                ]
            },] }
];
/** @nocollapse */
TmFroalaEditorModule.ctorParameters = () => [];

/**
 * @fileoverview added by tsickle
 * Generated from: public-api.ts
 * @suppress {checkTypes,constantProperty,extraRequire,missingOverride,missingReturn,unusedPrivateMembers,uselessCode} checked by tsc
 */

/**
 * @fileoverview added by tsickle
 * Generated from: techmore-froala-editor.ts
 * @suppress {checkTypes,constantProperty,extraRequire,missingOverride,missingReturn,unusedPrivateMembers,uselessCode} checked by tsc
 */

export { FROALA_ASSETS, FroalaEditorComponent, FroalaEditorModule, FroalaViewModule, TmFroalaEditorModule, froalaAssetsDefaultConfig, FroalaEditorDirective as ɵa, FroalaViewDirective as ɵb };
//# sourceMappingURL=techmore-froala-editor.js.map
